import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { io, Socket } from 'socket.io-client';
import { environment } from '../../../environments/environment';
import { DeliveryNoteNotification } from '../models/delivery-note.model';
import { LieferterminNotification } from '../models/order-document.model';

export enum SocketEvents {
  DELIVERY_NOTE_CREATED = 'deliveryNote:created',
  DELIVERY_NOTE_UPDATED = 'deliveryNote:updated',
  DELIVERY_NOTE_DELETED = 'deliveryNote:deleted',
  DELIVERY_NOTE_STATUS_CHANGED = 'deliveryNote:statusChanged',
  LIEFERTERMINE_CHANGED = 'liefertermine:changed',
}

@Injectable({
  providedIn: 'root',
})
export class SocketService implements OnDestroy {
  private socket: Socket | null = null;
  private isConnectedSubject = new BehaviorSubject<boolean>(false);

  // Delivery note event subjects
  private deliveryNoteCreated = new Subject<DeliveryNoteNotification>();
  private deliveryNoteUpdated = new Subject<DeliveryNoteNotification>();
  private deliveryNoteDeleted = new Subject<DeliveryNoteNotification>();
  private deliveryNoteStatusChanged = new Subject<DeliveryNoteNotification>();

  // Liefertermine event subject (production TV overview board)
  private liefertermineChanged = new Subject<LieferterminNotification>();

  public isConnected$ = this.isConnectedSubject.asObservable();
  public deliveryNoteCreated$ = this.deliveryNoteCreated.asObservable();
  public deliveryNoteUpdated$ = this.deliveryNoteUpdated.asObservable();
  public deliveryNoteDeleted$ = this.deliveryNoteDeleted.asObservable();
  public deliveryNoteStatusChanged$ = this.deliveryNoteStatusChanged.asObservable();
  public liefertermineChanged$ = this.liefertermineChanged.asObservable();

  constructor() {}

  connect(): void {
    if (this.socket?.connected) {
      return;
    }

    // Extract base URL without /api path
    const baseUrl = environment.apiUrl.replace('/api', '');

    this.socket = io(baseUrl, {
      transports: ['websocket', 'polling'],
      autoConnect: true,
    });

    this.socket.on('connect', () => {
      console.log('Socket connected:', this.socket?.id);
      this.isConnectedSubject.next(true);

      // Join delivery notes room
      this.socket?.emit('join:deliveryNotes');
      // Join liefertermine room (production TV overview board)
      this.socket?.emit('join:liefertermine');
    });

    this.socket.on('disconnect', () => {
      console.log('Socket disconnected');
      this.isConnectedSubject.next(false);
    });

    this.socket.on('connect_error', (error: Error) => {
      console.error('Socket connection error:', error);
      this.isConnectedSubject.next(false);
    });

    // Listen for delivery note events
    this.socket.on(SocketEvents.DELIVERY_NOTE_CREATED, (data: DeliveryNoteNotification) => {
      this.deliveryNoteCreated.next(data);
    });

    this.socket.on(SocketEvents.DELIVERY_NOTE_UPDATED, (data: DeliveryNoteNotification) => {
      this.deliveryNoteUpdated.next(data);
    });

    this.socket.on(SocketEvents.DELIVERY_NOTE_DELETED, (data: DeliveryNoteNotification) => {
      this.deliveryNoteDeleted.next(data);
    });

    this.socket.on(SocketEvents.DELIVERY_NOTE_STATUS_CHANGED, (data: DeliveryNoteNotification) => {
      this.deliveryNoteStatusChanged.next(data);
    });

    // Listen for liefertermine changes (production TV overview board)
    this.socket.on(SocketEvents.LIEFERTERMINE_CHANGED, (data: LieferterminNotification) => {
      this.liefertermineChanged.next(data);
    });
  }

  disconnect(): void {
    if (this.socket) {
      this.socket.emit('leave:deliveryNotes');
      this.socket.emit('leave:liefertermine');
      this.socket.disconnect();
      this.socket = null;
      this.isConnectedSubject.next(false);
    }
  }

  ngOnDestroy(): void {
    this.disconnect();
  }
}
